import { useCallback, useContext, useEffect, useRef, useState } from "react";
import classes from "./ImageEditor.module.css";
import useApiHttp from "../../hooks/use-http";
import { useParams } from "react-router-dom";
import { ActionButton, Icon } from "@jds/core";
import FilerobotImageEditor, {
  TABS,
  TOOLS,
} from "react-filerobot-image-editor";
import Loader from "../UI/Loader/Loader";
import { NewFileUploadContext } from "../Layout/NewFileUploadLayout/NewFileUploadLayout";
import { basetoFile, bytesToHumanReadable } from "../../util/helper";
import { file } from "jszip";
import cloudLogo from "../../assets/lottie/cloud.json";

import Lottie from "lottie-react";

const ImageEditor = ({
  toggler = false,
  imageObj = {},
  onClose = () => {},
  onSuccess = () => {},
}) => {
  const { id: eventId, fid: folderId } = useParams();
  const { uploadInChunks = () => {} } = useContext(NewFileUploadContext);

  const [image, setImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [editedImageBase, setEditedImageBase] = useState("");
  const [nameInput, setNameInput] = useState("edited_img");
  const inputRef = useRef(null);
  const [editName, setEditName] = useState(false);
  const [fileName, setFileName] = useState({
    name: "edited_img",
    ext: "jpeg",
  });
  const [fileDimension, setFileDimension] = useState({
    width: "",
    height: "",
  });
  const [fileUploadConformation, setFileUploadConformation] = useState(false);
  const [saveOption, setSaveOpiton] = useState("copy");
  const [uploadErr, setUploadErr] = useState({});
  const { sendRequest: downloadRequest } = useApiHttp();
  const { sendRequest: deleteImgReq, isLoading: deleteImgReqing } =
    useApiHttp();
  const [imgSaving, setImgSaving] = useState(false);
  // ---------------- DOWNLOAD IMAGE ----------------
  const downloadHandle = (mediaId) => {
    downloadRequest(
      {
        url: `organizer/download`,
        method: "POST",
        base: "MEDIA",
        body: { mediaId, eventId },
      },
      null,
      (data) => {
        const url = URL.createObjectURL(data);
        setImage(url);
      },
    );
  };

  // Action services
  const replaceImage = (mediaId) => {
    setImgSaving(true);
    const deleteIds = [mediaId];
    const formData = new FormData();
    formData.append("eventId", eventId);
    formData.append("mediaIDs", deleteIds);
    deleteImgReq(
      {
        url: `image/delete`,
        method: "DELETE",
        base: "MEDIA",
        formData: true,
        body: formData,
      },
      null,
      (data) => {
        handleUpload();
      },
      (err, data) => {
        setUploadErr(err.data);
        setImgSaving(false);
      },
    );
  };

  const saveFileName = useCallback(() => {
    setFileName({
      name: nameInput,
      ext: fileName.ext,
    });
    setEditName(false);
  }, [nameInput]);

  useEffect(() => {
    if (imageObj?.id) {
      downloadHandle(imageObj.id);
    }
  }, [imageObj?.id]);
  useEffect(() => {
    if (editedImageBase) {
      let fullName = fileName.name + "." + fileName.ext;
      setImageFile(basetoFile(editedImageBase, fullName));
    }
  }, [fileName, editedImageBase]);
  useEffect(() => {
    if (editName) {
      inputRef.current?.focus();
      inputRef.current?.select(); // Optional: selects the current text
    }
  }, [editName]);
  if (!toggler) return null;
  // ---------------- HANDLE UPLOAD ----------------
  const handleUpload = async () => {
    setImgSaving(true);
    try {
      const abortController = new AbortController();

      await uploadInChunks(
        imageFile,
        eventId,
        folderId,
        abortController.signal,
        "ADMIN",
      );
      setImgSaving(false);
      setFileUploadConformation(false);
      onClose();
      onSuccess();
    } catch (err) {
      setUploadErr(err.data);
      setImgSaving(false);
    }
  };

  return (
    <div className={classes["img-editor-overlay"]}>
      {image ? (
        <FilerobotImageEditor
          source={image}
          onSave={(editedImageObject) => {
            setEditedImageBase(editedImageObject.imageBase64);
            setFileName({
              name: editedImageObject.name,
              ext: editedImageObject.extension,
            });
            setNameInput(editedImageObject.name);
            setFileDimension({
              width: editedImageObject.width,
              height: editedImageObject.height,
            });
            setFileUploadConformation(true);
          }}
          onClose={onClose}
          tabsIds={[TABS.FINETUNE, TABS.FILTERS, TABS.ADJUST]}
          defaultTabId={TABS.FINETUNE}
          defaultToolId={TOOLS.BRIGHTNESS}
          defaultSavedImageName={`${imageObj?.fileName?.split(".")?.[0]}`}
          defaultSavedImageType="jpeg"
          theme={{
            palette: {
              "txt-primary": "#0078ae",
              "txt-placeholder": "#0078ae",
              "accent-primary-hover": "#0078ae",
              "accent-primary-active": "#0078ae",
              "accent-stateless": "#0078ae",
              "icon-primary": "#0078ae",
              warning: "#0078ae",
              "warning-hover": "#0078ae",
              "warning-active": "#0078ae",
              "orange-0-1-overlay": "#ecf3ff",
              "accent-primary": "#0078ae",
            },
          }}
          disableSaveIfNoChanges={true}
        />
      ) : (
        <div className={classes["img-editor-loader"]}>
          <div className={classes["img-editor-close"]}>
            <div
              onClick={onClose}
              style={{
                "--color-primary-grey-60": "white",
                cursor: "pointer",
              }}>
              <Icon ic="IcClose" color="grey-60" />
            </div>
          </div>

          <Loader />
        </div>
      )}

      {/* Upload Confirmation Modal */}
      {fileUploadConformation && (
        <div className={classes["img-upload-conformation"]}>
          <div className={classes["modal"]}>
            <p className={`${classes["modal-title"]}`}>Save Options</p>
            <div className={classes["modal-container"]}>
              <div className={classes["modal-info-container"]}>
                <p className={`${classes["modal-txt"]}`}>
                  Choose how you want to save this image
                </p>
                <div className={`${classes["img-del-card"]} `}>
                  <div className={`${classes["icon"]}`}>
                    <Icon ic={"IcPhoto"} kind="background" size="large" />
                  </div>
                  <div className={`${classes["img-info"]}`}>
                    {editName ? (
                      <div className={`${classes["filename-input"]}`}>
                        <input
                          ref={inputRef}
                          value={nameInput}
                          onChange={(e) => {
                            setUploadErr({});
                            setNameInput(e.target.value);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              saveFileName();
                            }
                          }}
                          onBlur={saveFileName}
                        />{" "}
                        <p className={`${classes["img-ext"]}`}>
                          .{fileName.ext}
                        </p>
                      </div>
                    ) : (
                      <div
                        className={`${classes["filename-txt"]}`}
                        onClick={() => {
                          setEditName(true);
                        }}>
                        <p className={`${classes["img-title"]}`}>
                          {fileName.name + "." + fileName.ext}
                        </p>
                        <Icon ic={"IcEditPen"} size="small" />
                      </div>
                    )}

                    <p className={`${classes["img-dec"]}`}>
                      {fileDimension?.width}*{fileDimension?.height} -{" "}
                      {bytesToHumanReadable(imageFile?.size)}
                    </p>
                  </div>
                </div>
                {uploadErr?.message && (
                  <div className={`${classes["upload-err"]}`}>
                    <p className={`${classes["err-txt"]}`}>
                      {" "}
                      {uploadErr?.message}
                    </p>
                  </div>
                )}
              </div>
              <div className={`${classes["save-input-container"]}`}>
                <div
                  className={`${classes["save-input-card"]} ${saveOption === "copy" ? classes["active"] : classes["unactive"]}`}
                  onClick={() => {
                    setSaveOpiton("copy");
                  }}>
                  <div className={`${classes["icon"]}`}>
                    <Icon ic={"IcCopy"} kind="background" size="large" />
                  </div>
                  <div className={`${classes["input-card-info"]}`}>
                    <p className={`${classes["input-card-title"]}`}>
                      Save a copy
                    </p>
                    <p className={`${classes["input-card-dec"]}`}>
                      Save as a new file - original file untouched.
                    </p>
                  </div>
                </div>
                <div
                  className={`${classes["save-input-card"]} ${saveOption === "replace" ? classes["active"] : classes["unactive"]}`}
                  onClick={() => {
                    setSaveOpiton("replace");
                  }}>
                  <div className={`${classes["icon"]}`}>
                    <Icon ic={"IcRefund"} kind="background" size="large" />
                  </div>
                  <div className={`${classes["input-card-info"]}`}>
                    <p className={`${classes["input-card-title"]}`}>
                      Replace original
                    </p>
                    <p className={`${classes["input-card-dec"]}`}>
                      Overwrites {imageObj.fileName} - this cannot be undone.
                    </p>
                  </div>
                </div>
              </div>
              {imgSaving && (
                <div className={`${classes["modal-loader"]}`}>
                  <div className={`${classes["loader-icon"]}`}>
                    <Lottie
                      animationData={cloudLogo}
                      loop={true}
                      autoplay={true}
                    />
                  </div>
                </div>
              )}
            </div>
            <div className={`${classes["modal-footer"]}`}>
              <div className={classes["modal-actions"]}>
                <ActionButton
                  kind="secondary"
                  label="Cancel"
                  onClick={() => {
                    if (!imgSaving) {
                      setUploadErr({});
                      setFileUploadConformation(false);
                    }
                  }}
                  state={"normal"}
                />

                <ActionButton
                  kind="primary"
                  label={"Save"}
                  onClick={() => {
                    if (!imgSaving) {
                      if (saveOption === "copy") {
                        handleUpload();
                      } else {
                        replaceImage(imageObj?.id);
                      }
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageEditor;
